import { useRef, useEffect } from "react";
import { Bold, Italic, Heading2, Heading3, List, Link2, Eraser } from "lucide-react";

/**
 * Lightweight rich text editor (contentEditable + execCommand).
 * Produces clean HTML so posts get bold, headings, lists and links with no markup.
 */
export default function RichTextEditor({ value, onChange, testid }) {
  const ref = useRef(null);

  // Initialise content once (uncontrolled to preserve caret position).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Enter URL (https://…)");
    if (url) exec("createLink", url);
  };

  const Btn = ({ onClick, icon: Icon, label, tid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={label}
      data-testid={tid}
      className="p-2 border border-white/15 text-white/70 hover:bg-white hover:text-black transition-colors"
    >
      <Icon size={14} />
    </button>
  );

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        <Btn onClick={() => exec("bold")} icon={Bold} label="Bold" tid="rte-bold" />
        <Btn onClick={() => exec("italic")} icon={Italic} label="Italic" tid="rte-italic" />
        <Btn onClick={() => exec("formatBlock", "<h2>")} icon={Heading2} label="Heading" tid="rte-h2" />
        <Btn onClick={() => exec("formatBlock", "<h3>")} icon={Heading3} label="Subheading" tid="rte-h3" />
        <Btn onClick={() => exec("insertUnorderedList")} icon={List} label="Bulleted list" tid="rte-ul" />
        <Btn onClick={addLink} icon={Link2} label="Link" tid="rte-link" />
        <Btn onClick={() => exec("removeFormat")} icon={Eraser} label="Clear formatting" tid="rte-clear" />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid={testid}
        className="rte-content min-h-[220px] max-h-[420px] overflow-y-auto bg-transparent border border-white/20 focus:border-white focus:outline-none font-mono text-sm text-white/85 p-4 leading-relaxed"
      />
      <p className="font-mono text-[10px] text-white/30 mt-2">Tip: select text, then use the toolbar. Links open in a new tab.</p>
    </div>
  );
}
